import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, Inbox, RefreshCw } from "lucide-react";
import { useClientAuth } from "@/context/ClientAuth";
import Seo from "@/components/Seo";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, loading, logout } = useClientAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/client/enquiries`, { credentials: "include" });
        if (r.ok) setEnquiries(await r.json());
      } finally {
        setBusy(false);
      }
    })();
  }, [user, loading, navigate]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-muted pt-24">
        <RefreshCw className="h-5 w-5 animate-spin mr-3" /> Loading your account…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg pt-32 pb-20" data-testid="account-page">
      <Seo title={`My Account | Arivu Finserve`} description="Your Arivu Finserve client account, profile and submitted enquiries." />
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-brand-border rounded-2xl p-8 lg:p-10">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover border border-brand-border" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center font-serif text-2xl">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-brand-green font-medium">Welcome back</p>
                <h1 className="font-serif text-3xl lg:text-4xl text-brand-navy mt-1">{user.name || user.email}</h1>
                <p className="text-brand-muted text-sm mt-1">{user.email}</p>
              </div>
            </div>
            <button onClick={logout} data-testid="client-logout" className="inline-flex items-center gap-2 rounded-full border border-brand-border text-brand-navy hover:bg-brand-navy hover:text-white text-sm px-5 py-2.5 transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href={SITE.calendly} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white text-sm font-medium px-6 py-3 hover:bg-brand-green transition-colors">
              Book a 30-min call
            </a>
            <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Avishaa, this is " + (user.name || user.email))}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#25D366] text-[#128C4B] hover:bg-[#25D366] hover:text-white text-sm font-medium px-6 py-3 transition-colors">
              Chat on WhatsApp
            </a>
          </div>
        </motion.div>

        <div className="mt-10">
          <div className="flex items-center gap-2 text-brand-muted mb-4">
            <Inbox className="h-4 w-4 text-brand-green" />
            <h2 className="text-xs uppercase tracking-[0.24em] font-semibold text-brand-navy">Your enquiries</h2>
          </div>
          {busy ? (
            <p className="text-brand-muted">Loading…</p>
          ) : enquiries.length === 0 ? (
            <div className="bg-white border border-dashed border-brand-border rounded-2xl p-8 text-brand-muted">
              You haven&apos;t sent any enquiries yet. Submit the contact form on the homepage and it will appear here.
            </div>
          ) : (
            <div className="space-y-4" data-testid="client-enquiries-list">
              {enquiries.map((c) => (
                <div key={c.id} className="bg-white border border-brand-border rounded-2xl p-6">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-serif text-lg text-brand-navy">{c.service || "General enquiry"}</p>
                    <span className="text-brand-muted text-sm">{fmt(c.created_at)}</span>
                  </div>
                  <p className="text-brand-navy/80 font-light mt-3 leading-relaxed">{c.message}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-brand-border text-sm text-brand-muted">
                    <span className="inline-flex items-center gap-2"><Mail className="h-4 w-4" /> {c.email}</span>
                    {c.phone && <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4" /> {c.phone}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
